import { Anchor, Breadcrumbs, Button } from "@mantine/core";
import {
  AppstoreOutlined,
  DatabaseOutlined,
  DollarOutlined,
  LaptopOutlined,
  NotificationOutlined,
  ProjectOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button as AntButton, Layout, Menu, Tooltip, theme } from "antd";
import { Link, useLocation, useParams } from "react-router-dom";
import React, { Children, ReactElement } from "react";
import { useEffect, useState } from "react";

import ChatBot, { isAiAssistantPath } from "@/components/ChatBot";
import Feature from "@/components/Feature";
import Header from "@/components/header";
import { Outlet } from "react-router-dom";
import SFC_logo from "/Logo/SFC_logo.jpg";
import aiAgentIcon from "/Logo/ai_agent.png";
import { apiUserGetEmployee } from "@/api/employee";
import { authClient } from "@/lib/auth-clients";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { rfqListPath } from "@/lib/rfq/api";
import routeMap from "../../routeMap";
import { useFeature } from "@/config/useFeature";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Sider, Content } = Layout;

const HRMainPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Create reverse mapping from translated keys to original Chinese keys
  const getOriginalKey = (translatedKey: string): string => {
    const reverseMap: { [key: string]: string } = {
      // Basic Info
      [t("hrMain.basicInfo")]: "基本資訊",
      [t("hrMain.companyCalendar")]: "行事曆",
      [t("hrMain.companyInfo")]: "公司資訊",
      [t("hrMain.employeeManagement")]: "員工管理",
      [t("hrMain.departmentManagement")]: "部門管理",
      [t("hrMain.subscriptionPlan")]: "訂閱方案",
      [t("hrMain.warRoom")]: "戰情室",

      // Partner
      [t("hrMain.partner")]: "合作方",
      [t("hrMain.partnerList")]: "合作方列表",
      [t("hrMain.underReviewStatus")]: "審核中",
      [t("hrMain.disabled")]: "已停用",

      // Project Management
      [t("hrMain.projectManagement")]: "專案管理",
      [t("hrMain.quotationStage")]: "專案列表",
      [t("hrMain.archivedProjects")]: "已封存",
      [t("hrMain.editQuotationStage")]: "編輯專案列表",
      [t("hrMain.productionPreparation")]: "生產準備階段",
      [t("hrMain.productionStage")]: "生產階段",

      // Purchase
      [t("hrMain.purchase")]: "採購",
      [t("hrMain.purchaseOrder")]: "採購單",
      [t("hrMain.addPurchaseOrder")]: "新增採購單",
      [t("hrMain.draft")]: "草稿",
      [t("hrMain.purchaseOrderDraft")]: "採購單草稿",
      [t("hrMain.purchaseOrderUnderReview")]: "採購單審核中",
      [t("hrMain.purchaseOrderApproved")]: "採購單已審核",
      [t("hrMain.purchaseOrderCompleted")]: "採購單已完成",
      [t("hrMain.rfq")]: "詢價單",
      [t("hrMain.rfqDraft")]: "詢價單草稿",
      [t("hrMain.rfqUnderReview")]: "詢價單審核中",
      [t("hrMain.rfqCompleted")]: "詢價單已完成",

      // Product
      [t("hrMain.product")]: "商品",
      [t("hrMain.productManagement")]: "商品管理",
      [t("hrMain.itemEditor")]: "品項編輯器",

      // Warehouse
      [t("hrMain.warehouse")]: "倉儲",
      [t("hrMain.warehouseLocation")]: "倉庫與儲位管理",
      [t("hrMain.stockHistory")]: "進出貨歷程",
      [t("hrMain.warehouseSystem")]: "倉儲管理系統",
      [t("hrMain.inventoryManagement")]: "入庫單管理",
      [t("hrMain.inventoryCreated")]: "已建立",
      [t("hrMain.inventoryDone")]: "已完成",
      [t("hrMain.inventoryManagementCreated")]: "入庫單管理已建立",
      [t("hrMain.inventoryManagementCompleted")]: "入庫單管理已完成",
      [t("hrMain.materialRequest")]: "領料單管理",
      [t("hrMain.materialRequestCreated")]: "已建立",
      [t("hrMain.materialRequestDone")]: "已完成",
      [t("hrMain.materialRequestCreatedPage")]: "領料單管理已建立",
      [t("hrMain.materialRequestCompletedPage")]: "領料單管理已完成",

      // Finance
      [t("hrMain.finance")]: "財務",
      [t("hrMain.accountsReceivable")]: "應收帳款(客戶)",
      [t("hrMain.accountsPayable")]: "應付帳款(供應商/信用卡/個人)",
      [t("hrMain.bank")]: "銀行",
    };

    return reverseMap[translatedKey] || translatedKey;
  };

  // Create mapping from Chinese keys to translated keys for breadcrumbs
  const getTranslatedKey = (chineseKey: string): string => {
    const translateMap: { [key: string]: string } = {
      // Basic Info
      基本資訊: t("hrMain.basicInfo"),
      行事曆: t("hrMain.companyCalendar"),
      公司資訊: t("hrMain.companyInfo"),
      員工管理: t("hrMain.employeeManagement"),
      員工管理系統: t("hrMain.employeeManagement"),
      編輯公司資訊: t("hrMain.companyInfo"),
      新增員工: t("hrMain.employeeManagement"),
      編輯員工: t("hrMain.employeeManagement"),
      新增部門: t("hrMain.departmentManagement"),
      部門管理: t("hrMain.departmentManagement"),
      訂閱方案: t("hrMain.subscriptionPlan"),
      戰情室: t("hrMain.warRoom"),

      // Partner
      合作方: t("hrMain.partner"),
      合作方列表: t("hrMain.partnerList"),
      新增合作方: t("hrMain.partnerList"),
      審核中: t("hrMain.underReviewStatus"),
      已停用: t("hrMain.disabled"),

      // Project Management
      專案管理: t("hrMain.projectManagement"),
      專案列表: t("hrMain.quotationStage"),
      已封存: t("hrMain.archivedProjects"),
      新增專案列表: t("hrMain.quotationStage"),
      編輯專案列表: t("hrMain.editQuotationStage"),
      專案列表顯示: t("hrMain.quotationStage"),

      // Purchase
      採購: t("hrMain.purchase"),
      採購單: t("hrMain.purchaseOrder"),
      新增採購單: t("hrMain.addPurchaseOrder"),
      編輯採購單: t("hrMain.purchaseOrder"),
      採購單草稿: t("hrMain.purchaseOrderDraft"),
      採購單審核中: t("hrMain.purchaseOrderUnderReview"),
      採購單已審核: t("hrMain.purchaseOrderApproved"),
      採購單已完成: t("hrMain.purchaseOrderCompleted"),
      詢價單: t("hrMain.rfq"),
      詢價單草稿: t("hrMain.rfqDraft"),
      詢價單審核中: t("hrMain.rfqUnderReview"),
      詢價單已完成: t("hrMain.rfqCompleted"),

      // Product
      商品: t("hrMain.product"),
      商品管理: t("hrMain.productManagement"),
      新增商品: t("hrMain.productManagement"),
      品項編輯器: t("hrMain.itemEditor"),

      // Warehouse
      倉儲: t("hrMain.warehouse"),
      倉庫與儲位管理: t("hrMain.warehouseLocation"),
      進出貨歷程: t("hrMain.stockHistory"),
      倉儲管理系統: t("hrMain.warehouseSystem"),
      新增倉庫: t("hrMain.warehouseLocation"),
      入庫單管理: t("hrMain.inventoryManagement"),
      入庫單管理已建立: t("hrMain.inventoryManagementCreated"),
      建立入庫單: t("hrMain.inventoryManagementCreated"),
      入庫單管理已完成: t("hrMain.inventoryManagementCompleted"),
      領料單管理: t("hrMain.materialRequest"),
      領料單管理已建立: t("hrMain.materialRequestCreatedPage"),
      領料單管理已完成: t("hrMain.materialRequestCompletedPage"),

      // Finance
      財務: t("hrMain.finance"),
      "應收帳款(客戶)": t("hrMain.accountsReceivable"),
      "新增應收帳款(客戶)": t("hrMain.accountsReceivable"),
      "應付帳款(供應商/信用卡/個人)": t("hrMain.accountsPayable"),
      銀行: t("hrMain.bank"),
    };

    return translateMap[chineseKey] || chineseKey;
  };

  const getMenuIcon = (label: string) => {
    switch (label) {
      case t("hrMain.basicInfo"):
        return React.createElement(UserOutlined);
      case t("hrMain.partner"):
        return React.createElement(TeamOutlined);
      case t("hrMain.projectManagement"):
        return React.createElement(ProjectOutlined);
      case t("hrMain.purchase"):
        return React.createElement(ShoppingCartOutlined);
      case t("hrMain.product"):
        return React.createElement(AppstoreOutlined);
      case t("hrMain.warehouse"):
        return React.createElement(DatabaseOutlined);
      case t("hrMain.finance"):
        return React.createElement(DollarOutlined);
      default:
        return React.createElement(LaptopOutlined);
    }
  };
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const companyCalendarEnabled = useFeature("companyCalendar");

  const hasFullAccess =
    userRoles.includes("owner") || userRoles.includes("admin");

  const tempData = [
    {
      key: t("hrMain.basicInfo"),
      icon: getMenuIcon(t("hrMain.basicInfo")),
      label: t("hrMain.basicInfo"),
      children: [
        ...(companyCalendarEnabled
          ? [
              {
                key: t("hrMain.companyCalendar"),
                label: t("hrMain.companyCalendar"),
              },
            ]
          : []),
        {
          key: t("hrMain.companyInfo"),
          label: t("hrMain.companyInfo"),
        },
        {
          key: t("hrMain.employeeManagement"),
          label: t("hrMain.employeeManagement"),
        },
        {
          key: t("hrMain.warRoom"),
          label: t("hrMain.warRoom"),
        },
        // {
        //   key: "部門管理",
        //   label: "部門管理",
        // },
        // {
        //   key: "訂閱方案",
        //   label: "訂閱方案",
        // },
      ],
    },
    {
      key: t("hrMain.partner"),
      icon: getMenuIcon(t("hrMain.partner")),
      label: t("hrMain.partner"),
      children: [
        {
          key: t("hrMain.partnerList"),
          label: t("hrMain.partnerList"),
        },
        {
          key: t("hrMain.underReviewStatus"),
          label: t("hrMain.underReviewStatus"),
        },
        {
          key: t("hrMain.disabled"),
          label: t("hrMain.disabled"),
        },
      ],
    },
    {
      key: t("hrMain.projectManagement"),
      icon: getMenuIcon(t("hrMain.projectManagement")),
      label: t("hrMain.projectManagement"),
      children: [
        {
          key: t("hrMain.quotationStage"),
          label: t("hrMain.quotationStage"),
        },
        {
          key: t("hrMain.archivedProjects"),
          label: t("hrMain.archivedProjects"),
        },
        // {
        //   key: t("hrMain.editQuotationStage"),
        //   label: t("hrMain.editQuotationStage"),
        // },
        // {
        //   key: t("hrMain.productionPreparation"),
        //   label: t("hrMain.productionPreparation"),
        // },
        // {
        //   key: t("hrMain.productionStage"),
        //   label: t("hrMain.productionStage"),
        // },
      ],
    },
    {
      key: t("hrMain.purchase"),
      icon: getMenuIcon(t("hrMain.purchase")),
      label: t("hrMain.purchase"),
      children: [
        {
          key: t("hrMain.purchaseOrder"),
          label: t("hrMain.purchaseOrder"),
          children: [
            {
              key: t("hrMain.purchaseOrderDraft"),
              label: t("hrMain.draft"),
            },
            {
              key: t("hrMain.purchaseOrderUnderReview"),
              label: t("hrMain.underReviewStatus"),
            },
            {
              key: t("hrMain.purchaseOrderApproved"),
              label: t("hrMain.approved"),
            },
            {
              key: t("hrMain.purchaseOrderCompleted"),
              label: t("hrMain.completed"),
            },
          ],
        },
        {
          key: t("hrMain.rfq"),
          label: t("hrMain.rfq"),
          children: [
            {
              key: t("hrMain.rfqDraft"),
              label: t("hrMain.draft"),
            },
            {
              key: t("hrMain.rfqUnderReview"),
              label: t("hrMain.underReviewStatus"),
            },
            {
              key: t("hrMain.rfqCompleted"),
              label: t("hrMain.completed"),
            },
          ],
        },
        // {
        //   key: '托外加工單',
        //   label: '托外加工單',
        //   children: [
        //     {
        //       key: '托外加工單草稿',
        //       label: '草稿',
        //     },
        //     {
        //       key: '托外加工單審核中',
        //       label: '審核中',
        //     },
        //     {
        //       key: '托外加工單已審核',
        //       label: '已審核',
        //     },
        //     {
        //       key: '托外加工單已完成',
        //       label: '已完成',
        //     },
        //   ],
        // },
        // {
        //   key: '檢驗單',
        //   label: '檢驗單',
        //   children: [],
        // },
        // {
        //   key: '退貨單',
        //   label: '退貨單',
        //   children: [],
        // },
      ],
    },
    {
      key: t("hrMain.product"),
      icon: getMenuIcon(t("hrMain.product")),
      label: t("hrMain.product"),
      children: [
        {
          key: t("hrMain.productManagement"),
          label: t("hrMain.productManagement"),
        },
        {
          key: t("hrMain.itemEditor"),
          label: t("hrMain.itemEditor"),
        },
      ],
    },
    {
      key: t("hrMain.warehouse"),
      icon: getMenuIcon(t("hrMain.warehouse")),
      label: t("hrMain.warehouse"),
      children: [
        {
          key: t("hrMain.warehouseLocation"),
          label: t("hrMain.warehouseLocation"),
        },
        {
          key: t("hrMain.stockHistory"),
          label: t("hrMain.stockHistory"),
        },
        {
          key: t("hrMain.inventoryManagement"),
          label: t("hrMain.inventoryManagement"),
          children: [
            {
              key: t("hrMain.inventoryManagementCreated"),
              label: t("hrMain.inventoryCreated"),
            },
            {
              key: t("hrMain.inventoryManagementCompleted"),
              label: t("hrMain.completed"),
            },
          ],
        },
        {
          key: t("hrMain.materialRequest"),
          label: t("hrMain.materialRequest"),
          children: [
            {
              key: t("hrMain.materialRequestCreatedPage"),
              label: t("hrMain.materialRequestCreated"),
            },
            {
              key: t("hrMain.materialRequestCompletedPage"),
              label: t("hrMain.completed"),
            },
          ],
        },
      ],
    },
    {
      key: t("hrMain.finance"),
      icon: getMenuIcon(t("hrMain.finance")),
      label: t("hrMain.finance"),
      children: [
        {
          key: t("hrMain.accountsReceivable"),
          label: t("hrMain.accountsReceivable"),
        },
        {
          key: t("hrMain.accountsPayable"),
          label: t("hrMain.accountsPayable"),
        },
        {
          key: t("hrMain.bank"),
          label: t("hrMain.bank"),
        },
      ],
    },
  ].filter((item) => {
    if (item.key === t("hrMain.projectManagement"))
      return hasFullAccess || userRoles.includes("project");
    if (item.key === t("hrMain.purchase"))
      return (
        hasFullAccess ||
        userRoles.includes("purchase") ||
        userRoles.includes("purchaseManager")
      );
    if (item.key === t("hrMain.product"))
      return hasFullAccess || userRoles.includes("inventory");
    if (item.key === t("hrMain.warehouse"))
      return hasFullAccess || userRoles.includes("inventory");
    if (item.key === t("hrMain.finance"))
      return hasFullAccess || userRoles.includes("finance");
    return true;
  });
  const [currSteps, setCurrSteps] = useState<ReactElement[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const [employeeName, setEmployeeName] = useState<string>("");
  const [partnerName, setPartnerName] = useState<string>("");
  const [purchaseOrderName, setPurchaseOrderName] = useState<string>("");
  const [purchaseOrderStatus, setPurchaseOrderStatus] = useState<string>("");
  const [rfqName, setRfqName] = useState<string>("");
  const [rfqStatus, setRfqStatus] = useState<string>("");
  const [warehouseName, setWarehouseName] = useState<string>("");
  const [productName, setProductName] = useState<string>("");
  const [inventoryCreatedName, setInventoryCreatedName] = useState<string>("");
  const [inventoryDoneName, setInventoryDoneName] = useState<string>("");
  const [stockRequestCreatedName, setStockRequestCreatedName] =
    useState<string>("");
  const [stockRequestDoneName, setStockRequestDoneName] = useState<string>("");
  const [accountReceivableName, setAccountReceivableName] = useState<string>("");
  const [accountPayableName, setAccountPayableName] = useState<string>("");
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const showAiChat = isAiAssistantPath(pathname);

  useEffect(() => {
    if (!isAiAssistantPath(pathname)) {
      setChatOpen(false);
    }
  }, [pathname]);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const result = await authClient.organization.getActiveMember();
        if (result?.data?.role) {
          const roles = [
            ...new Set<string>(
              (result.data.role as string)
                .split(",")
                .map((r: string) => r.trim())
                .filter(Boolean)
            ),
          ];
          setUserRoles(roles);
        }
      } catch {
      }
    };
    fetchUserRoles();
  }, []);

  const getMenuKeysFromPath = (
    path: string
  ): { selectedKeys: string[]; openKeys: string[] } => {
    const cleanedPath = path.startsWith("/") ? path.slice(1) : path;

    const pathToMenuMap: {
      [key: string]: { selected: string; parent: string };
    } = {
      "hrMainPage/coop": {
        selected: t("hrMain.partnerList"),
        parent: t("hrMain.partner"),
      },
      "hrMainPage/addCoop": {
        selected: t("hrMain.partnerList"),
        parent: t("hrMain.partner"),
      },
      "hrMainPage/coopPending": {
        selected: t("hrMain.underReviewStatus"),
        parent: t("hrMain.partner"),
      },
      "hrMainPage/coopReviewed": {
        selected: t("hrMain.disabled"),
        parent: t("hrMain.partner"),
      },

      // Basic Info routes
      "hrMainPage/calendar": {
        selected: t("hrMain.companyCalendar"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/companyInfo": {
        selected: t("hrMain.companyInfo"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/editCompanyInfo": {
        selected: t("hrMain.companyInfo"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/employee": {
        selected: t("hrMain.employeeManagement"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/employees": {
        selected: t("hrMain.employeeManagement"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/warRoom": {
        selected: t("hrMain.warRoom"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/addEmployee": {
        selected: t("hrMain.employeeManagement"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/editEmployee": {
        selected: t("hrMain.employeeManagement"),
        parent: t("hrMain.basicInfo"),
      },

      // Project Management routes
      "hrMainPage/projectQuotation": {
        selected: t("hrMain.quotationStage"),
        parent: t("hrMain.projectManagement"),
      },
      "hrMainPage/projectQuotationArchived": {
        selected: t("hrMain.archivedProjects"),
        parent: t("hrMain.projectManagement"),
      },
      "hrMainPage/editQuotation": {
        selected: t("hrMain.quotationStage"),
        parent: t("hrMain.projectManagement"),
      },
      "hrMainPage/quotationPage": {
        selected: t("hrMain.quotationStage"),
        parent: t("hrMain.projectManagement"),
      },

      // Warehouse routes
      "hrMainPage/warehouse": {
        selected: t("hrMain.warehouseLocation"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/warehouseSystem": {
        selected: t("hrMain.warehouseSystem"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/stockHistory": {
        selected: t("hrMain.stockHistory"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/addWarehouse": {
        selected: t("hrMain.warehouseLocation"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/product": {
        selected: t("hrMain.productManagement"),
        parent: t("hrMain.product"),
      },
      "hrMainPage/addProduct": {
        selected: t("hrMain.productManagement"),
        parent: t("hrMain.product"),
      },
      "hrMainPage/itemEditor": {
        selected: t("hrMain.itemEditor"),
        parent: t("hrMain.product"),
      },
      "hrMainPage/inventoryCreated": {
        selected: t("hrMain.inventoryManagement"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/addInventoryCreated": {
        selected: t("hrMain.inventoryManagement"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/inventoryDone": {
        selected: t("hrMain.inventoryManagement"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/stockRequestCreated": {
        selected: t("hrMain.materialRequest"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/stockRequestDone": {
        selected: t("hrMain.materialRequest"),
        parent: t("hrMain.warehouse"),
      },

      // Finance routes
      "hrMainPage/accountPayable": {
        selected: t("hrMain.accountsPayable"),
        parent: t("hrMain.finance"),
      },
      "hrMainPage/editAccountPayable": {
        selected: t("hrMain.accountsPayable"),
        parent: t("hrMain.finance"),
      },
      "hrMainPage/accountReceivable": {
        selected: t("hrMain.accountsReceivable"),
        parent: t("hrMain.finance"),
      },
      "hrMainPage/editAccountReceivable": {
        selected: t("hrMain.accountsReceivable"),
        parent: t("hrMain.finance"),
      },
      "hrMainPage/bank": {
        selected: t("hrMain.bank"),
        parent: t("hrMain.finance"),
      },

      // Purchase routes
      "hrMainPage/addPurchaseOrder": {
        selected: t("hrMain.purchaseOrder"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/purchaseOrderDraft": {
        selected: t("hrMain.purchaseOrderDraft"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/purchaseOrderUnderReview": {
        selected: t("hrMain.purchaseOrderUnderReview"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/purchaseOrderApproved": {
        selected: t("hrMain.purchaseOrderApproved"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/purchaseOrderCompleted": {
        selected: t("hrMain.purchaseOrderCompleted"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/rfqDraft": {
        selected: t("hrMain.rfqDraft"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/rfqUnderReview": {
        selected: t("hrMain.rfqUnderReview"),
        parent: t("hrMain.purchase"),
      },
      "hrMainPage/rfqCompleted": {
        selected: t("hrMain.rfqCompleted"),
        parent: t("hrMain.purchase"),
      },
    };

    // Check for exact matches first
    if (pathToMenuMap[cleanedPath]) {
      const matchedRoute = pathToMenuMap[cleanedPath];
      return {
        selectedKeys: [matchedRoute.selected],
        openKeys: [matchedRoute.parent],
      };
    }

    // Check for dynamic routes (with IDs) - more specific patterns
    const dynamicRoutePatterns: {
      [key: string]: { selected: string; parent: string };
    } = {
      "hrMainPage/coop/": {
        selected: t("hrMain.partnerList"),
        parent: t("hrMain.partner"),
      },
      "hrMainPage/editCoop/": {
        selected: t("hrMain.partnerList"),
        parent: t("hrMain.partner"),
      },

      "hrMainPage/employee/": {
        selected: t("hrMain.employeeManagement"),
        parent: t("hrMain.basicInfo"),
      },
      "hrMainPage/editEmployee/": {
        selected: t("hrMain.employeeManagement"),
        parent: t("hrMain.basicInfo"),
      },

      "hrMainPage/projectQuotation/": {
        selected: t("hrMain.quotationStage"),
        parent: t("hrMain.projectManagement"),
      },
      "hrMainPage/editQuotation/": {
        selected: t("hrMain.quotationStage"),
        parent: t("hrMain.projectManagement"),
      },

      "hrMainPage/warehouse/": {
        selected: t("hrMain.warehouseLocation"),
        parent: t("hrMain.warehouse"),
      },
      "hrMainPage/product/": {
        selected: t("hrMain.productManagement"),
        parent: t("hrMain.product"),
      },

      "hrMainPage/purchaseOrder/": {
        selected: t("hrMain.purchaseOrder"),
        parent: t("hrMain.purchase"),
      },

      "hrMainPage/editAccountReceivable/": {
        selected: t("hrMain.accountsReceivable"),
        parent: t("hrMain.finance"),
      },

      "hrMainPage/editAccountPayable/": {
        selected: t("hrMain.accountsPayable"),
        parent: t("hrMain.finance"),
      },
    };

    for (const pattern in dynamicRoutePatterns) {
      if (cleanedPath.startsWith(pattern)) {
        const matchedRoute = dynamicRoutePatterns[pattern];
        return {
          selectedKeys: [matchedRoute.selected],
          openKeys: [matchedRoute.parent],
        };
      }
    }

    if (cleanedPath.startsWith("hrMainPage/")) {
      // Try to match the base route segment
      const segments = cleanedPath.split("/");
      if (segments.length >= 2) {
        const baseRoute = segments.slice(0, 2).join("/");
        if (pathToMenuMap[baseRoute]) {
          const matchedRoute = pathToMenuMap[baseRoute];
          return {
            selectedKeys: [matchedRoute.selected],
            openKeys: [matchedRoute.parent],
          };
        }
      }
    }

    return {
      selectedKeys: [],
      openKeys: [],
    };
  };

  const onClick = (e: any) => {
    const routePath: string[] = [];

    e?.keyPath.forEach((keys: string) => {
      // Convert translated keys back to original Chinese keys for routeMap lookup
      const originalKey = getOriginalKey(keys);
      routePath.push(originalKey);
    });

    function getRoute(pathArray: string[], obj: any): string | undefined {
      let current = obj;

      for (let i = 0; i < pathArray.length; i++) {
        if (current[pathArray[i]]) {
          current = current[pathArray[i]];

          if (i === pathArray.length - 1) {
            return current;
          }
          i = -1;
        }
      }

      return undefined;
    }
    const route = "/" + getRoute(routePath?.reverse(), routeMap);
    //routeMap
    navigate(route);
  };

  useEffect(() => {
    // Extract project ID from URL if it's a project-related page (BOM or quotation)
    const cleanedUrl = pathname.startsWith("/") ? pathname.slice(1) : pathname;
    const projectPatterns = [
      "hrMainPage/steelAssemblyBOM/",
      "hrMainPage/addSteelAssemblyBOM/",
      "hrMainPage/steelBoltBOM/",
      "hrMainPage/addSteelBoltBOM/",
      "hrMainPage/designBOM/",
      "hrMainPage/addDesignBOM/",
      "hrMainPage/projectQuotation/",
      "hrMainPage/editQuotation/",
    ];

    const matchingPattern = projectPatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingPattern) {
      const isDesignBOMPattern =
        cleanedUrl.startsWith("hrMainPage/designBOM/") ||
        cleanedUrl.startsWith("hrMainPage/addDesignBOM/");
      const searchParams = new URLSearchParams(search);
      const quotationId = searchParams.get("quotationId");
      const id = isDesignBOMPattern && quotationId
        ? quotationId
        : cleanedUrl.split("/")[2];
      if (id) {
        const fetchProjectName = async () => {
          try {
            const result = await single(orpcClient.project.read([{ id }]));
            if (result?.name) {
              setProjectName(result.name);
            }
          } catch (error) {
            console.error("Error fetching project name:", error);
          }
        };
        fetchProjectName();
      }
    } else {
      // Clear project name when not on a project-related page
      setProjectName("");
    }

    // Extract employee ID from URL if it's an employee-related page
    const employeePatterns = [
      "hrMainPage/employee/",
      "hrMainPage/editEmployee/",
    ];

    const matchingEmployeePattern = employeePatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingEmployeePattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchEmployeeName = async () => {
          try {
            const result = await apiUserGetEmployee({ id });
            if (result?.data?.name) {
              setEmployeeName(result.data.name);
            }
          } catch (error) {
            console.error("Error fetching employee name:", error);
          }
        };
        fetchEmployeeName();
      }
    } else {
      // Clear employee name when not on an employee-related page
      setEmployeeName("");
    }

    // Extract partner ID from URL if it's a partner-related page
    const partnerPatterns = ["hrMainPage/coop/", "hrMainPage/editCoop/"];

    const matchingPartnerPattern = partnerPatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingPartnerPattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchPartnerName = async () => {
          try {
            const result = await single(orpcClient.partner.read([{ id }]));
            if (result?.name) {
              setPartnerName(result.name);
            }
          } catch (error) {
            console.error("Error fetching partner name:", error);
          }
        };
        fetchPartnerName();
      }
    } else {
      setPartnerName("");
    }

    // Extract purchase order ID from URL if it's a purchase order-related page
    const purchaseOrderPatterns = [
      "hrMainPage/purchaseOrder/",
      "hrMainPage/editPurchaseOrder/",
      "hrMainPage/purchaseOrderDraft/",
      "hrMainPage/purchaseOrderUnderReview/",
      "hrMainPage/purchaseOrderApproved/",
      "hrMainPage/purchaseOrderCompleted/",
    ];

    const matchingPurchaseOrderPattern = purchaseOrderPatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingPurchaseOrderPattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchPurchaseOrderName = async () => {
          try {
            const result = await single(orpcClient.purchaseOrder.read([{ id }]));
            if (result?.name) {
              setPurchaseOrderName(result.name);
            }
            if (result?.status) {
              setPurchaseOrderStatus(result.status);
            }
          } catch (error) {
            console.error("Error fetching purchase order name:", error);
          }
        };
        fetchPurchaseOrderName();
      }
    } else {
      setPurchaseOrderName("");
      setPurchaseOrderStatus("");
    }

    // Extract RFQ ID from URL if it's an RFQ detail page
    const rfqMatch = cleanedUrl.match(/^hrMainPage\/rfq\/([^/]+)$/);
    if (rfqMatch) {
      const id = rfqMatch[1];
      const fetchRfqName = async () => {
        try {
          const result = await single(orpcClient.rfq.read([{ id }]));
          setRfqName(result?.name || result?.code || "");
          setRfqStatus(result?.status || "");
        } catch (error) {
          console.error("Error fetching rfq name:", error);
        }
      };
      fetchRfqName();
    } else {
      setRfqName("");
      setRfqStatus("");
    }

    // Extract warehouse ID from URL if it's a warehouse-related page
    const warehousePatterns = [
      "hrMainPage/warehouse/",
      "hrMainPage/editWarehouse/",
    ];

    const matchingWarehousePattern = warehousePatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingWarehousePattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchWarehouseName = async () => {
          try {
            const result = await single(orpcClient.warehouse.read([{ id }]));
            if (result?.name) {
              setWarehouseName(result.name);
            }
          } catch (error) {
            console.error("Error fetching warehouse name:", error);
          }
        };
        fetchWarehouseName();
      }
    } else {
      // Clear warehouse name when not on a warehouse-related page
      setWarehouseName("");
    }

    // Extract product ID from URL if it's a product-related page
    const productPatterns = ["hrMainPage/product/", "hrMainPage/editProduct/"];

    const matchingProductPattern = productPatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingProductPattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchProductName = async () => {
          try {
            const result = await single(orpcClient.product.read([{ id }]));
            if (result?.name) {
              setProductName(result.name);
            }
          } catch (error) {
            console.error("Error fetching product name:", error);
          }
        };
        fetchProductName();
      }
    } else {
      setProductName("");
    }

    const inventoryCreatedPatterns = [
      "hrMainPage/inventoryCreated/",
      "hrMainPage/editInventoryCreated/",
    ];

    const matchingInventoryCreatedPattern = inventoryCreatedPatterns.find(
      (pattern) => cleanedUrl.startsWith(pattern)
    );
    if (matchingInventoryCreatedPattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchInventoryCreatedName = async () => {
          try {
            const result = await single(orpcClient.stockReceipt.read([{ id }]));
            if (result?.name) {
              setInventoryCreatedName(result.name);
            }
          } catch (error) {
            console.error("Error fetching inventory created name:", error);
          }
        };
        fetchInventoryCreatedName();
      }
    } else {
      setInventoryCreatedName("");
    }

    const inventoryDonePatterns = [
      "hrMainPage/inventoryDone/",
      "hrMainPage/editInventoryDone/",
    ];

    const matchingInventoryDonePattern = inventoryDonePatterns.find((pattern) =>
      cleanedUrl.startsWith(pattern)
    );
    if (matchingInventoryDonePattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchInventoryDoneName = async () => {
          try {
            const result = await single(orpcClient.stockReceipt.read([{ id }]));
            if (result?.name) {
              setInventoryDoneName(result.name);
            }
          } catch (error) {
            console.error("Error fetching inventory done name:", error);
          }
        };
        fetchInventoryDoneName();
      }
    } else {
      setInventoryDoneName("");
    }

    const stockRequestCreatedPatterns = [
      "hrMainPage/stockRequestCreated/",
      "hrMainPage/editStockRequestCreated/",
    ];

    const matchingStockRequestCreatedPattern = stockRequestCreatedPatterns.find(
      (pattern) => cleanedUrl.startsWith(pattern)
    );
    if (matchingStockRequestCreatedPattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchStockRequestCreatedName = async () => {
          try {
            const result = await single(orpcClient.stockRequest.read([{ id }]));
            if (result?.name) {
              setStockRequestCreatedName(result.name);
            }
          } catch (error) {
            console.error("Error fetching stock request created name:", error);
          }
        };
        fetchStockRequestCreatedName();
      }
    } else {
      setStockRequestCreatedName("");
    }

    const stockRequestDonePatterns = [
      "hrMainPage/stockRequestDone/",
      "hrMainPage/editStockRequestDone/",
    ];

    const matchingStockRequestDonePattern = stockRequestDonePatterns.find(
      (pattern) => cleanedUrl.startsWith(pattern)
    );
    if (matchingStockRequestDonePattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchStockRequestDoneName = async () => {
          try {
            const result = await single(orpcClient.stockRequest.read([{ id }]));
            if (result?.name) {
              setStockRequestDoneName(result.name);
            }
          } catch (error) {
            console.error("Error fetching stock request done name:", error);
          }
        };
        fetchStockRequestDoneName();
      }
    } else {
      setStockRequestDoneName("");
    }

    const accountReceivablePatterns = ["hrMainPage/editAccountReceivable/"];

    const matchingAccountReceivablePattern = accountReceivablePatterns.find(
      (pattern) => cleanedUrl.startsWith(pattern)
    );
    if (matchingAccountReceivablePattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchAccountReceivableName = async () => {
          try {
            const result = await single(orpcClient.accountsReceivable.read([{ id }]));
            if (result?.name) {
              setAccountReceivableName(result.name);
            }
          } catch (error) {
            console.error("Error fetching account receivable name:", error);
          }
        };
        fetchAccountReceivableName();
      }
    } else {
      setAccountReceivableName("");
    }

    const accountPayablePatterns = ["hrMainPage/editAccountPayable/"];

    const matchingAccountPayablePattern = accountPayablePatterns.find(
      (pattern) => cleanedUrl.startsWith(pattern)
    );
    if (matchingAccountPayablePattern) {
      const id = cleanedUrl.split("/")[2];
      if (id) {
        const fetchAccountPayableName = async () => {
          try {
            const result = await single(orpcClient.accountsPayable.read([{ id }]));
            if (result?.name) {
              setAccountPayableName(result.name);
            }
          } catch (error) {
            console.error("Error fetching account payable name:", error);
          }
        };
        fetchAccountPayableName();
      }
    } else {
      setAccountPayableName("");
    }
  }, [pathname, search]);

  useEffect(() => {
    const menuState = getMenuKeysFromPath(pathname);
    setSelectedKeys(menuState.selectedKeys);
    setOpenKeys((prevOpenKeys) => {
      const newOpenKeys = [...menuState.openKeys];
      prevOpenKeys.forEach((key) => {
        if (!newOpenKeys.includes(key)) {
          newOpenKeys.push(key);
        }
      });
      return newOpenKeys;
    });
  }, [pathname, i18n.language]);

  useEffect(() => {
    function generateBreadcrumbs(url: string) {
      const breadcrumbs: Array<{ label: string; path: string | null }> = [];
      const cleanedUrl = url.startsWith("/") ? url.slice(1) : url;
      const pathToBreadcrumb: {
        [key: string]: Array<{ label: string; path: string | null }>;
      } = {};

      if (cleanedUrl.startsWith("hrMainPage/steelAssemblyBOM/")) {
        const id = cleanedUrl.split("/")[2]; // Extract the ID
        breadcrumbs.push({ label: t("hrMain.projectManagement"), path: null }); // Not clickable
        breadcrumbs.push({
          label: t("hrMain.quotationStage"),
          path: "/hrMainPage/projectQuotation",
        }); // Clickable
        breadcrumbs.push({
          label: projectName || id,
          path: `/hrMainPage/projectQuotation/${id}`,
        });
        breadcrumbs.push({ label: t("hrMain.materialBOM"), path: null }); // Not clickable
        return breadcrumbs;
      }

      if (cleanedUrl.startsWith("hrMainPage/addSteelAssemblyBOM/")) {
        const id = cleanedUrl.split("/")[2]; // Extract the ID
        breadcrumbs.push({ label: t("hrMain.projectManagement"), path: null }); // Not clickable
        breadcrumbs.push({
          label: t("hrMain.quotationStage"),
          path: "/hrMainPage/projectQuotation",
        }); // Clickable
        breadcrumbs.push({
          label: projectName || id,
          path: `/hrMainPage/projectQuotation/${id}`,
        });
        breadcrumbs.push({ label: t("hrMain.addMaterialBOM"), path: null }); // Not clickable
        return breadcrumbs;
      }

      if (cleanedUrl.startsWith("hrMainPage/steelBoltBOM/")) {
        const id = cleanedUrl.split("/")[2]; // Extract the ID
        breadcrumbs.push({ label: t("hrMain.projectManagement"), path: null }); // Not clickable
        breadcrumbs.push({
          label: t("hrMain.quotationStage"),
          path: "/hrMainPage/projectQuotation",
        }); // Clickable
        breadcrumbs.push({
          label: projectName || id,
          path: `/hrMainPage/projectQuotation/${id}`,
        });
        breadcrumbs.push({ label: t("hrMain.boltBOM"), path: null }); // Not clickable
        return breadcrumbs;
      }

      if (cleanedUrl.startsWith("hrMainPage/addSteelBoltBOM/")) {
        const id = cleanedUrl.split("/")[2]; // Extract the ID
        breadcrumbs.push({ label: t("hrMain.projectManagement"), path: null }); // Not clickable
        breadcrumbs.push({
          label: t("hrMain.quotationStage"),
          path: "/hrMainPage/projectQuotation",
        }); // Clickable
        breadcrumbs.push({
          label: projectName || id,
          path: `/hrMainPage/projectQuotation/${id}`,
        });
        breadcrumbs.push({ label: t("hrMain.addBoltBOM"), path: null }); // Not clickable
        return breadcrumbs;
      }

      if (cleanedUrl.startsWith("hrMainPage/designBOM/")) {
        const buildingId = cleanedUrl.split("/")[2];
        const searchParams = new URLSearchParams(search);
        const quotationId = searchParams.get("quotationId") || buildingId;

        breadcrumbs.push({ label: t("hrMain.projectManagement"), path: null }); // Not clickable
        breadcrumbs.push({
          label: t("hrMain.quotationStage"),
          path: "/hrMainPage/projectQuotation",
        }); // Clickable
        breadcrumbs.push({
          label: projectName || quotationId,
          path: `/hrMainPage/projectQuotation/${quotationId}`,
        });
        breadcrumbs.push({ label: t("hrMain.designBOM"), path: null }); // Not clickable
        return breadcrumbs;
      }

      if (cleanedUrl.startsWith("hrMainPage/addDesignBOM/")) {
        const buildingId = cleanedUrl.split("/")[2];
        const searchParams = new URLSearchParams(search);
        const quotationId = searchParams.get("quotationId") || buildingId;

        breadcrumbs.push({ label: t("hrMain.projectManagement"), path: null }); // Not clickable
        breadcrumbs.push({
          label: t("hrMain.quotationStage"),
          path: "/hrMainPage/projectQuotation",
        }); // Clickable
        breadcrumbs.push({
          label: projectName || quotationId,
          path: `/hrMainPage/projectQuotation/${quotationId}`,
        });
        breadcrumbs.push({ label: t("hrMain.addDesignBOM"), path: null }); // Not clickable
        return breadcrumbs;
      }

      for (const [category, subRoutes] of Object.entries(routeMap)) {
        if (typeof subRoutes === "object") {
          for (const [subLabel, subPath] of Object.entries(subRoutes)) {
            if (typeof subPath === "string") {
              pathToBreadcrumb[subPath] = [
                { label: getTranslatedKey(category), path: null }, // Category should not be clickable
                { label: getTranslatedKey(subLabel), path: `/${subPath}` }, // Subpage should be clickable
              ];
            }
          }
        }
      }

      const fixedExceptions = [
        {
          pattern: /^hrMainPage\/inventoryCreated$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.inventoryManagement"),
          label: t("hrMain.inventoryCreated"),
        },
        {
          pattern: /^hrMainPage\/inventoryDone$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.inventoryManagement"),
          label: t("hrMain.completed"),
        },
        {
          pattern: /^hrMainPage\/stockRequestCreated$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.materialRequest"),
          label: t("hrMain.materialRequestCreated"),
        },
        {
          pattern: /^hrMainPage\/stockRequestDone$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.materialRequest"),
          label: t("hrMain.completed"),
        },
        {
          pattern: /^hrMainPage\/addPurchaseOrder$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.purchaseOrder"),
          label: t("hrMain.addPurchaseOrder"),
        },
        {
          pattern: /^hrMainPage\/purchaseOrderDraft$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.purchaseOrder"),
          label: t("hrMain.draft"),
        },
        {
          pattern: /^hrMainPage\/addInventoryCreated$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.inventoryManagement"),
          label: t("hrMain.inventoryManagementCreated"),
        },
        {
          pattern: /^hrMainPage\/addInventoryDone$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.inventoryManagement"),
          label: t("hrMain.inventoryManagementCompleted"),
        },
        {
          pattern: /^hrMainPage\/addStockRequestCreated$/,
          category: t("hrMain.warehouse"),
          subCategory: t("hrMain.materialRequest"),
          label: t("hrMain.addStockRequest"),
        },
        {
          pattern: /^hrMainPage\/purchaseOrderUnderReview$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.purchaseOrder"),
          label: t("hrMain.underReviewStatus"),
        },
        {
          pattern: /^hrMainPage\/purchaseOrderApproved$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.purchaseOrder"),
          label: t("hrMain.approved"),
        },
        {
          pattern: /^hrMainPage\/purchaseOrderCompleted$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.purchaseOrder"),
          label: t("hrMain.completed"),
        },
        {
          pattern: /^hrMainPage\/rfqDraft$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.rfq"),
          label: t("hrMain.draft"),
        },
        {
          pattern: /^hrMainPage\/rfqUnderReview$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.rfq"),
          label: t("hrMain.underReviewStatus"),
        },
        {
          pattern: /^hrMainPage\/rfqCompleted$/,
          category: t("hrMain.purchase"),
          subCategory: t("hrMain.rfq"),
          label: t("hrMain.completed"),
        },
      ];

      for (const exception of fixedExceptions) {
        if (cleanedUrl.match(exception.pattern)) {
          breadcrumbs.push({ label: exception.category, path: null }); // 倉儲 (Not clickable)
          breadcrumbs.push({ label: exception.subCategory, path: null }); // 入庫單管理 (Not clickable)
          breadcrumbs.push({ label: exception.label, path: `/${cleanedUrl}` }); // Clickable last breadcrumb
          return breadcrumbs;
        }
      }

      // RFQ detail: 採購 > 詢價單 > 草稿|審核中|已完成（依狀態）> 詢價單名稱
      const rfqDetailMatch = cleanedUrl.match(/^hrMainPage\/rfq\/([^/]+)$/);
      if (rfqDetailMatch) {
        const rfqListLabel =
          rfqStatus === "underReview"
            ? t("hrMain.underReviewStatus")
            : rfqStatus === "completed"
              ? t("hrMain.completed")
              : t("hrMain.draft");
        breadcrumbs.push({ label: t("hrMain.purchase"), path: null });
        breadcrumbs.push({ label: t("hrMain.rfq"), path: null });
        breadcrumbs.push({
          label: rfqListLabel,
          path: rfqListPath(rfqStatus),
        });
        breadcrumbs.push({
          label: rfqName || rfqDetailMatch[1],
          path: pathname,
        });
        return breadcrumbs;
      }

      // Handle dynamic pages with "/:id"
      const dynamicPatterns = [
        {
          pattern: /^hrMainPage\/coop\/(.+)$/,
          category: t("hrMain.partner"),
          listPath: "hrMainPage/coop",
        },
        {
          pattern: /^hrMainPage\/editCoop\/(.+)$/,
          category: t("hrMain.partner"),
          listPath: "hrMainPage/coop",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/department\/(.+)$/,
          category: t("hrMain.basicInfo"),
          listPath: "hrMainPage/department",
        },
        {
          pattern: /^hrMainPage\/editDepartment\/(.+)$/,
          category: t("hrMain.basicInfo"),
          listPath: "hrMainPage/department",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/employee\/(.+)$/,
          category: t("hrMain.basicInfo"),
          listPath: "hrMainPage/employees",
        },
        {
          pattern: /^hrMainPage\/editEmployee\/(.+)$/,
          category: t("hrMain.basicInfo"),
          listPath: "hrMainPage/employees",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/projectQuotation\/(.+)$/,
          category: t("hrMain.projectManagement"),
          listPath: "hrMainPage/projectQuotation",
        },
        {
          pattern: /^hrMainPage\/editQuotation\/(.+)$/,
          category: t("hrMain.projectManagement"),
          listPath: "hrMainPage/projectQuotation",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/purchaseOrder\/(.+)$/,
          category: t("hrMain.purchase"),
          listPath: "hrMainPage/purchaseOrderDraft",
          subCategory: t("hrMain.purchaseOrder"),
        },
        {
          pattern: /^hrMainPage\/editPurchaseOrder\/(.+)$/,
          category: t("hrMain.purchase"),
          listPath: "hrMainPage/purchaseOrderDraft",
          subCategory: t("hrMain.purchaseOrder"),
          edit: true,
        },
        {
          pattern: /^hrMainPage\/warehouse\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/warehouse",
        },
        {
          pattern: /^hrMainPage\/editWarehouse\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/warehouse",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/product\/(.+)$/,
          category: t("hrMain.product"),
          listPath: "hrMainPage/product",
        },
        {
          pattern: /^hrMainPage\/editProduct\/(.+)$/,
          category: t("hrMain.product"),
          listPath: "hrMainPage/product",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/inventoryCreated\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/inventoryCreated",
          subCategory: t("hrMain.inventoryManagement"),
        },
        {
          pattern: /^hrMainPage\/editInventoryCreated\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/inventoryCreated",
          subCategory: t("hrMain.inventoryManagement"),
          edit: true,
        },
        {
          pattern: /^hrMainPage\/inventoryDone\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/inventoryDone",
          subCategory: t("hrMain.inventoryManagement"),
        },
        {
          pattern: /^hrMainPage\/editInventoryDone\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/inventoryDone",
          subCategory: t("hrMain.inventoryManagement"),
          edit: true,
        },
        {
          pattern: /^hrMainPage\/stockRequestCreated\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/stockRequestCreated",
          subCategory: t("hrMain.materialRequest"),
        },
        {
          pattern: /^hrMainPage\/editStockRequestCreated\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/stockRequestCreated",
          subCategory: t("hrMain.materialRequest"),
          edit: true,
        },
        {
          pattern: /^hrMainPage\/stockRequestDone\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/stockRequestDone",
          subCategory: t("hrMain.materialRequest"),
        },
        {
          pattern: /^hrMainPage\/editStockRequestDone\/(.+)$/,
          category: t("hrMain.warehouse"),
          listPath: "hrMainPage/stockRequestDone",
          subCategory: t("hrMain.materialRequest"),
          edit: true,
        },
        {
          pattern: /^hrMainPage\/editAccountReceivable\/(.+)$/,
          category: t("hrMain.finance"),
          listPath: "hrMainPage/accountReceivable",
          edit: true,
        },
        {
          pattern: /^hrMainPage\/editAccountPayable\/(.+)$/,
          category: t("hrMain.finance"),
          listPath: "hrMainPage/accountPayable",
          edit: true,
        },
      ];

      let matched = false;
      for (const {
        pattern,
        category,
        listPath,
        edit,
        subCategory,
      } of dynamicPatterns) {
        const match = cleanedUrl.match(pattern);
        if (match) {
          breadcrumbs.push({ label: category, path: null }); // Category is not clickable

          // Add subCategory if it exists
          if (subCategory) {
            breadcrumbs.push({ label: subCategory, path: null }); // SubCategory is not clickable
          }

          // Find the correct list page label
          const getPurchaseOrderListPath = (status: string) => {
            switch (status) {
              case "draft":
                return "hrMainPage/purchaseOrderDraft";
              case "underReview":
                return "hrMainPage/purchaseOrderUnderReview";
              case "approved":
                return "hrMainPage/purchaseOrderApproved";
              case "completed":
                return "hrMainPage/purchaseOrderCompleted";
              default:
                return "hrMainPage/purchaseOrderDraft";
            }
          };

          const getPurchaseOrderListLabel = (status: string) => {
            switch (status) {
              case "draft":
                return t("hrMain.draft");
              case "underReview":
                return t("hrMain.underReviewStatus");
              case "approved":
                return t("hrMain.approved");
              case "completed":
                return t("hrMain.completed");
              default:
                return t("hrMain.draft");
            }
          };

          const actualListPath =
            cleanedUrl.startsWith("hrMainPage/purchaseOrder/") &&
            purchaseOrderStatus
              ? getPurchaseOrderListPath(purchaseOrderStatus)
              : listPath;

          const listLabel =
            Object.entries(routeMap)
              .flatMap(([cat, sub]) =>
                Object.entries(sub).map(([key, value]) => ({
                  key,
                  value,
                  cat,
                }))
              )
              .find((entry) => entry.value === actualListPath)?.key ||
            // Special handling for purchase order status pages
            (cleanedUrl.startsWith("hrMainPage/purchaseOrder/") &&
            purchaseOrderStatus
              ? getPurchaseOrderListLabel(purchaseOrderStatus)
              : t("hrMain.list"));

          breadcrumbs.push({
            label: getTranslatedKey(listLabel),
            path: `/${actualListPath}`,
          }); // Clickable list page

          const label = edit
            ? listPath === "hrMainPage/employees" && employeeName
              ? employeeName
              : listPath === "hrMainPage/coop" && partnerName
                ? partnerName
                : listPath === "hrMainPage/department"
                  ? t("hrMain.departmentManagement")
                  : listPath === "hrMainPage/projectQuotation"
                    ? projectName || match[1]
                    : listPath === "hrMainPage/warehouse" && warehouseName
                      ? warehouseName
                      : listPath === "hrMainPage/product" && productName
                        ? productName
                        : listPath === "hrMainPage/inventoryCreated" &&
                            inventoryCreatedName
                          ? inventoryCreatedName
                          : listPath === "hrMainPage/inventoryDone" &&
                              inventoryDoneName
                            ? inventoryDoneName
                            : listPath === "hrMainPage/stockRequestCreated" &&
                                stockRequestCreatedName
                              ? stockRequestCreatedName
                              : listPath === "hrMainPage/stockRequestDone" &&
                                  stockRequestDoneName
                                ? stockRequestDoneName
                                : listPath ===
                                      "hrMainPage/purchaseOrderDraft" &&
                                    purchaseOrderName
                                  ? purchaseOrderName
                                  : listPath ===
                                        "hrMainPage/accountReceivable" &&
                                      accountReceivableName
                                    ? accountReceivableName
                                    : listPath ===
                                          "hrMainPage/accountPayable" &&
                                        accountPayableName
                                      ? accountPayableName
                                      : match[1]
            : listPath === "hrMainPage/projectQuotation"
              ? projectName || match[1]
              : listPath === "hrMainPage/employees" && employeeName
                ? employeeName
                : listPath === "hrMainPage/coop" && partnerName
                  ? partnerName
                  : listPath === "hrMainPage/warehouse" && warehouseName
                    ? warehouseName
                    : listPath === "hrMainPage/product" && productName
                      ? productName
                      : (listPath === "hrMainPage/purchaseOrderDraft" ||
                            cleanedUrl.startsWith(
                              "hrMainPage/purchaseOrder/"
                            )) &&
                          purchaseOrderName
                        ? purchaseOrderName
                        : listPath === "hrMainPage/inventoryCreated" &&
                            inventoryCreatedName
                          ? inventoryCreatedName
                          : listPath === "hrMainPage/inventoryDone" &&
                              inventoryDoneName
                            ? inventoryDoneName
                            : listPath === "hrMainPage/stockRequestCreated" &&
                                stockRequestCreatedName
                              ? stockRequestCreatedName
                              : listPath === "hrMainPage/stockRequestDone" &&
                                  stockRequestDoneName
                                ? stockRequestDoneName
                                : match[1];
          breadcrumbs.push({ label, path: pathname });

          matched = true;
          break;
        }
      }

      // Handle static pages
      if (!matched && pathToBreadcrumb[cleanedUrl]) {
        breadcrumbs.push(...pathToBreadcrumb[cleanedUrl]);
      }

      return breadcrumbs;
    }

    const breadcrumbs = generateBreadcrumbs(pathname);

    const steps: ReactElement[] = breadcrumbs.map((item, index) => (
      <span key={index}>
        {item.path ? (
          <Link to={item.path} className="text-blue-500 hover:underline">
            {item.label}
          </Link>
        ) : (
          <span className="text-gray-500">{item.label}</span>
        )}
      </span>
    ));

    setCurrSteps(steps);
  }, [
    pathname,
    search,
    projectName,
    employeeName,
    partnerName,
    purchaseOrderName,
    purchaseOrderStatus,
    rfqName,
    rfqStatus,
    warehouseName,
    productName,
    inventoryCreatedName,
    inventoryDoneName,
    stockRequestCreatedName,
    stockRequestDoneName,
    accountReceivableName,
    accountPayableName,
    i18n.language,
  ]);

  return (
    <Layout
      className="h-screen overflow-hidden"
      style={{ backgroundColor: "rgb(236, 236, 236)" }}
    >
      <Sider
        className="w-3/10 h-screen overflow-y-auto overflow-x-hidden bg-gray-100"
        style={{ background: colorBgContainer }}
      >
        <img src={SFC_logo} alt="SFC Logo" className="w-full p-4" />
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onClick={(e) => onClick(e)}
          items={tempData}
        />
      </Sider>
      <Layout
        className="w-7/10 h-screen  "
        style={{ backgroundColor: "#ECECEC", flexDirection: "row" }}
      >
        <Content className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
          <Header />
          <div
            className="flex items-center justify-between gap-3 p-4"
            style={{ backgroundColor: "#E4E4E4" }}
          >
            <Breadcrumbs>{currSteps}</Breadcrumbs>
            {showAiChat && (
              <Feature flag="genAiChat">
                <Tooltip title="AI 助理">
                  <AntButton
                    type="default"
                    shape="circle"
                    icon={
                      <img
                        src={aiAgentIcon}
                        alt="AI 助理"
                        style={{ width: 20, height: 20, objectFit: "contain" }}
                      />
                    }
                    onClick={() => setChatOpen((o) => !o)}
                    className="shrink-0"
                  />
                </Tooltip>
              </Feature>
            )}
          </div>
          <div
            className="min-h-0 flex-1 overflow-auto"
            style={{ backgroundColor: "#ECECEC" }}
          >
            <Outlet />
          </div>
        </Content>
        {showAiChat && (
          <Feature flag="genAiChat">
            <ChatBot docked open={chatOpen} onClose={() => setChatOpen(false)} />
          </Feature>
        )}
      </Layout>
    </Layout>
  );
};

export default HRMainPage;
